import { forEachSchemaNode } from '@framework/validation/describe/forEachSchemaNode.ts';
import { type IAdvancedRichText } from '../../../../inputs/rich-text-advanced/IAdvancedRichText.ts';
import { isRichTextSchema } from '../../../../inputs/rich-text-advanced/schema/RichTextAdvancedMeta.ts';
import { RootWidgetSchema } from '../../../../validator/root/RootWidgetSchema.ts';
import { type IRootWidget } from '../../../widgets/repository/containers/root/IRootWidget.ts';
import { parseDataReference } from '../../compute/input/nodes/ComputeExpressionReferenceNode.tsx';
import { isReferenceSchema } from '../../validation/isReferenceSchema.ts';
import { type IReference } from '../IReference.ts';

/**
 * Regex per estrarre le reference in formato testo salvate dentro un rich text.
 * Con matchAll, è possibile estrarre `match[1]`che contiene il valore
 * codificato della reference (decodificabile con `parseDataReference`).
 */
const ADVANCED_RICH_TEXT_REFERENCE_REGEX =
  /<span [^>]* data-type="compute-expression-reference" [^>]* data-reference="([a-zA-Z0-9+/=]+)">@([^<]+)<\/span>/gm;

interface Props {
  root: IRootWidget;
}

/**
 * Funzione che estrae tutte le reference nello schema.
 */
export function collectSchemaReferences(props: Props): IReference[] {
  const { root } = props;

  const results: IReference[] = [];

  forEachSchemaNode(RootWidgetSchema, { value: root }, node => {
    // CHECK: Reference "normale" (default, regole, computed expression)
    if (isReferenceSchema(node.schema) && node.value != null) {
      results.push(node.value);
    }

    // CHECK: Reference in rich text
    if (isRichTextSchema(node.schema) && node.value != null) {
      const references = extractAdvancedRichTextReferences(node.value);
      results.push(...references);
    }
  });

  return results;
}

/**
 * Estrae le reference presenti in un advanced rich text.
 */
export function extractAdvancedRichTextReferences(
  richText: IAdvancedRichText,
): IReference[] {
  const references: IReference[] = [];

  richText.forEach(v => {
    // Controllo che il testo contenga riferimenti
    // Uso una nuova regex altrimenti, essendo global,
    // il matchAll dentro extractRichTextReferences non funziona correttamente
    // partendo dalla fine della stringa e ritornando 0 risultati.
    if (v.text && new RegExp(ADVANCED_RICH_TEXT_REFERENCE_REGEX).test(v.text)) {
      const extracted = extractRichTextReferences(v.text);
      references.push(...extracted);
    }
  });

  return references;
}

/**
 * Estrae le reference da una stringa di rich text.
 */
export function extractRichTextReferences(richText: string): IReference[] {
  if (!richText) return [];

  const matches = [...richText.matchAll(ADVANCED_RICH_TEXT_REFERENCE_REGEX)];
  const references: IReference[] = [];

  for (const match of matches) {
    const encoded = match[1];
    if (!encoded) continue;
    const reference = parseDataReference(encoded);
    if (reference) {
      references.push(reference);
    }
  }

  return references;
}
